import React from 'react';

import { Link, useParams } from 'react-router-dom';

import ClosedBadge from '../../components/common/ClosedBadge';
import Loader from '../../components/common/Loader';
import {
  MessageCard,
  OwnerAvatar,
  SharedPageLayout,
} from '../../components/common/SharedPage';
import PlayedCountFlag from '../../components/maps/PlayedCountFlag';
import { useSharedCoursesPlayed } from '../../hooks/useCoursesPlayedShare';
import { SharedCoursePlayedDTO } from '../../services/apiClient';
import { courseLogo } from '../../utils/courseClosure';

// A member's Courses Played list as anyone with the link sees it: /shared/courses-played/:shareId.
// Public, no sign-in. The code in the URL is a share code the member created by pressing Share on
// their own list (and can revoke), never their member id.
//
// Every way the link can be wrong ends on a plain message, not an error screen: a malformed code
// is caught before any request (useSharedCoursesPlayed), and an unknown or revoked code, a network
// failure and a list with no courses each have their own line. Nothing the visitor types can reach
// the server except a well-formed GUID.
//
// What a stranger sees is deliberately thin (the member's first name, last initial and profile
// picture, and each course's rank, name, logo and place), in the same charcoal and gold as the
// public course page (the frame is SharedPageLayout, shared with a shared profile).

const plural = (count: number, one: string, many: string): string =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;

// "445 Courses in 16 Countries and 52 States", counted off the list itself. A state is counted
// with its country, so two countries' "Georgia" stay two states. A part with nothing to count is
// left out rather than shown as "0 States".
const listSummary = (courses: SharedCoursePlayedDTO[]): string => {
  const countries = new Set(
    courses.map((c) => c.countryName).filter((name) => !!name),
  );
  const states = new Set(
    courses
      .filter((c) => !!c.stateName)
      .map((c) => `${c.countryName ?? ''}|${c.stateName}`),
  );

  let summary = plural(courses.length, 'Course', 'Courses');
  if (countries.size > 0) {
    summary += ` in ${plural(countries.size, 'Country', 'Countries')}`;
  }
  if (states.size > 0) {
    summary += `${countries.size > 0 ? ' and' : ' in'} ${plural(states.size, 'State', 'States')}`;
  }
  return summary;
};

const CourseRow: React.FC<{ course: SharedCoursePlayedDTO }> = ({ course }) => {
  const place = [course.stateName, course.countryName]
    .filter(Boolean)
    .join(', ');

  const name = (
    <span className="block truncate font-medium text-white">
      {course.courseName || 'Unknown course'}
    </span>
  );

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="w-8 shrink-0 text-right text-base font-semibold tabular-nums text-primary">
        {course.rank}
      </span>

      {/* White plate and the GolfPlayed mark for a club with no logo, as everywhere on the map. */}
      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white p-0.5 ring-1 ring-black/20">
        <img
          src={courseLogo(course).src}
          alt=""
          className="h-full w-full object-contain"
          loading="lazy"
          draggable={false}
        />
      </span>

      <span className="min-w-0 flex-1">
        {/* Only courses the public course page can show are links; the rest are plain text
            rather than a link to "not found". */}
        {course.hasProfile && course.courseId != null ? (
          <Link
            to={`/courses/${course.courseId}`}
            className="block rounded underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {name}
          </Link>
        ) : (
          name
        )}
        {(place || course.closed) && (
          <span className="flex min-w-0 items-center gap-1.5">
            {place && (
              <span className="truncate text-xs text-white/50">{place}</span>
            )}
            <ClosedBadge
              closed={course.closed}
              closedYear={course.closedYear}
              closedMonth={course.closedMonth}
            />
          </span>
        )}
      </span>
    </li>
  );
};

const SharedCoursesPlayed: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const state = useSharedCoursesPlayed(shareId);
  const courses = state.status === 'ready' ? (state.list.courses ?? []) : [];

  return (
    <SharedPageLayout showCallToAction={state.status === 'ready'}>
      {state.status === 'loading' && (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      )}

      {state.status === 'invalid' && (
        <MessageCard
          title="This link isn't valid."
          body="Check that the whole link was copied, then try again."
        />
      )}

      {state.status === 'notShared' && (
        <MessageCard
          title="This list isn't shared."
          body="The link may be old, or its owner may have stopped sharing it."
        />
      )}

      {state.status === 'error' && (
        <MessageCard
          title="Could not load this list."
          body="Check your connection and try again."
        />
      )}

      {state.status === 'ready' && (
        <>
          {/* Picture, name, then the flag with the count: the same mark a member sees on their
                own account button on the map. */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <OwnerAvatar
                name={state.list.ownerName ?? ''}
                pictureUrl={state.list.ownerPictureUrl}
              />
              <h1 className="min-w-0 break-words font-display text-2xl font-bold leading-tight">
                {state.list.ownerName}
              </h1>
              <PlayedCountFlag count={courses.length} className="h-8" />
            </div>
            <p className="mt-3 font-display text-sm font-semibold text-primary">
              {listSummary(courses)}
            </p>
          </div>

          {courses.length === 0 ? (
            <p className="rounded-xl border border-white/10 bg-charcoal-light px-6 py-8 text-center text-sm text-white/60">
              No courses on this list yet.
            </p>
          ) : (
            <ol className="divide-y divide-white/10 overflow-hidden rounded-xl border border-primary/30 bg-charcoal-light">
              {courses.map((course) => (
                <CourseRow key={course.courseId} course={course} />
              ))}
            </ol>
          )}
        </>
      )}
    </SharedPageLayout>
  );
};

export default SharedCoursesPlayed;
